import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import GoldDivider from "@/components/GoldDivider";
import Reveal from "@/components/Reveal";

const services = [
  {
    number: "01",
    title: "Capital Advisory & Syndication",
    tagline: "Placing capital with precision.",
    desc: "We facilitate the deployment of capital from family offices, HNIs, and institutional investors into curated, high-conviction real estate opportunities. Our advisory is selective, structured, and strictly confidential.",
    sectors: ["Family Office Mandates", "HNI Syndication", "Institutional Placement"],
  },
  {
    number: "02",
    title: "Developer & Platform Partnerships",
    tagline: "Building long-term development platforms.",
    desc: "We work with India's leading developers to structure joint ventures, capital stacks, and development platforms. Our relationships run deep — across promoter families, institutional JV partners, and PE-backed platforms.",
    sectors: ["Joint Ventures", "Capital Stack Structuring", "Platform Development"],
  },
  {
    number: "03",
    title: "Real Estate Investment Banking",
    tagline: "Executing complex transactions with discretion.",
    desc: "Private equity transactions, structured deals, and strategic capital placement across commercial, land, hospitality, and mixed-use asset classes. We operate at the deal level that most advisors cannot reach.",
    sectors: ["PE Transactions", "Structured Capital", "Asset Class Advisory"],
  },
  {
    number: "04",
    title: "Portfolio Strategy & Optimization",
    tagline: "Maximising the value of what you hold.",
    desc: "Advisory on portfolio construction, yield enhancement, asset repositioning, and exit strategies for family offices and institutional investors with existing real estate exposure.",
    sectors: ["Portfolio Construction", "Yield Enhancement", "Exit Strategy"],
  },
  {
    number: "05",
    title: "Leasing, Monetisation & Asset Stabilisation",
    tagline: "Turning assets into performing assets.",
    desc: "Strategic leasing across commercial, retail, and hospitality assets. We drive occupancy, anchor tenant placement, and income optimisation to stabilise assets and maximise investor returns.",
    sectors: ["Commercial Leasing", "Retail & Hospitality", "Income Optimisation"],
  },
  {
    number: "06",
    title: "Corporate & Industrial Advisory",
    tagline: "Real estate strategy for growing organisations.",
    desc: "Land acquisition, built-to-suit solutions, campus advisory, and corporate real estate strategy for large corporates, industrial occupiers, and multinational companies entering India.",
    sectors: ["Land Acquisition", "Built-to-Suit", "Corporate Strategy"],
  },
];

function PageHero() {
  return (
    <section
      data-testid="services-hero"
      className="relative pt-40 pb-28 px-6 sm:px-12 lg:px-24 overflow-hidden"
      style={{ background: "linear-gradient(to bottom, #040A12 0%, #081323 100%)" }}
    >
      <div className="max-w-5xl mx-auto">
        <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#C6A75E]/80 block mb-6 reveal visible">
          Capabilities
        </span>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-white tracking-tight leading-tight mb-8 reveal visible reveal-delay-2">
          How We<br />
          <span className="text-[#C6A75E]/80">Create Value</span>
        </h1>
        <div className="h-px w-14 bg-[#C6A75E]/50 mb-8 reveal visible reveal-delay-3" />
        <p className="font-sans text-base font-light text-slate-400 leading-relaxed max-w-2xl reveal visible reveal-delay-4">
          Six institutional capabilities — each one built on deep expertise, exclusive relationships, and a strategy-first mandate.
        </p>
      </div>
    </section>
  );
}

function ServicesGrid() {
  return (
    <section data-testid="services-grid" className="py-16 px-6 sm:px-12 lg:px-24 max-w-7xl mx-auto">
      <div className="space-y-0">
        {services.map((svc, i) => (
          <Reveal key={svc.number} delay={i * 80}>
            <div
              data-testid={`service-item-${i}`}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 py-12 border-b border-[#C6A75E]/10 hover:border-[#C6A75E]/25 transition-colors duration-600 group"
            >
              {/* Number */}
              <div className="lg:col-span-1">
                <span className="font-serif text-3xl font-light text-[#C6A75E]/20 group-hover:text-[#C6A75E]/40 transition-colors duration-600">
                  {svc.number}
                </span>
              </div>

              {/* Title col */}
              <div className="lg:col-span-4">
                <h3 className="font-serif text-xl sm:text-2xl font-light text-white mb-2 group-hover:text-[#C6A75E]/90 transition-colors duration-600 leading-snug">
                  {svc.title}
                </h3>
                <p className="font-sans text-xs italic text-[#C6A75E]/50 font-light">{svc.tagline}</p>
              </div>

              {/* Description */}
              <div className="lg:col-span-4">
                <p className="font-sans text-sm font-light text-slate-400 leading-loose">{svc.desc}</p>
              </div>

              {/* Sectors */}
              <div className="lg:col-span-3">
                <div className="space-y-2">
                  {svc.sectors.map((s) => (
                    <div key={s} className="flex items-center gap-2">
                      <div className="w-1 h-1 rounded-full bg-[#C6A75E]/40" />
                      <span className="font-sans text-[10px] uppercase tracking-[0.15em] text-slate-500">{s}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ServicesCTA() {
  return (
    <section className="py-24 px-6 sm:px-12 lg:px-24 bg-[#081323]/50 border-t border-[#C6A75E]/10">
      <div className="max-w-4xl mx-auto text-center">
        <Reveal>
          <p className="font-serif text-2xl sm:text-3xl font-light text-white mb-6">
            An engagement begins with a conversation.
          </p>
        </Reveal>
        <Reveal delay={200}>
          <p className="font-sans text-sm font-light text-slate-500 mb-10">
            All mandates are handled with complete confidentiality.
          </p>
        </Reveal>
        <Reveal delay={350}>
          <Link
            to="/contact"
            data-testid="services-cta"
            className="inline-flex items-center gap-3 px-10 py-4 border border-[#C6A75E]/60 text-[#C6A75E] font-sans text-[10px] uppercase tracking-[0.25em] hover:bg-[#C6A75E]/8 hover:border-[#C6A75E] transition-all duration-600"
          >
            Start a Conversation
            <ArrowRight size={12} strokeWidth={1} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Services() {
  return (
    <div className="bg-[#040A12]" data-testid="services-page">
      <PageHero />
      <GoldDivider />
      <ServicesGrid />
      <GoldDivider />
      <ServicesCTA />
    </div>
  );
}
